import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Coins, ExternalLink } from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { REVENUE_FIELDS, fmt, fmtAxis, tooltipStyle } from "./oversiktConstants";
import { ShareTable, ShareCompareChart } from "./ShareCompare";
import { dec } from "@/lib/formatters";

const pct = (value, total) => (total ? `${dec(((value / total) * 100), 1)} %` : "");

// averages: { fylkeName, fylke: [{year, kommune_count, share}], landName, land: [{year, share}] }
const RevenueSection = ({ data, averages }) => {
  const rows = (data || []).filter((d) => d.revenue?.sum_driftsinntekter);
  const latest = rows[rows.length - 1];
  const latestTotal = latest?.revenue.sum_driftsinntekter || 0;
  const fylkeLatest = latest ? averages?.fylke?.find((d) => d.year === latest.year) : null;
  const landLatest = latest ? averages?.land?.find((d) => d.year === latest.year) : null;

  const shareRows = latest
    ? REVENUE_FIELDS.map((f) => ({
        key: f.key,
        name: f.label,
        value: latest.revenue[f.key] || 0,
        share: latestTotal ? ((latest.revenue[f.key] || 0) / latestTotal) * 100 : 0,
        fylkeShare: fylkeLatest?.share?.[f.key] ?? null,
        landShare: landLatest?.share?.[f.key] ?? null,
        color: f.color,
      })).sort((a, b) => b.value - a.value)
    : [];
  const pieData = shareRows.filter((d) => d.value > 0);

  const barData = rows.map((d) => {
    const point = { year: d.year, _total: d.revenue.sum_driftsinntekter };
    REVENUE_FIELDS.forEach((f) => { point[f.label] = d.revenue[f.key] ?? 0; });
    return point;
  });

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="revenue-section">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Coins className="w-5 h-5 text-[#103766]" />
          Kor kjem inntektene frå?
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Driftsinntekter etter hovudgruppe, kommunekonsern (1000 kr)</p>
      </CardHeader>
      <CardContent className="pt-6">
        {rows.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-[#8AA4BF]" data-testid="revenue-empty">
            Ingen inntektsdata tilgjengeleg – klikk «Oppdater data»
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              <div className="xl:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                  Fordeling {latest.year}
                </p>
                <p className="text-sm text-[#4B647F] mb-2">Sum driftsinntekter: {fmt(latestTotal)} tusen kr</p>
                <div className="h-[260px]" data-testid="revenue-pie-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={105} paddingAngle={2} stroke="white">
                        {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                      </Pie>
                      <Tooltip formatter={(v, name) => [`${fmt(v)} tusen kr (${pct(v, latestTotal)})`, name]} contentStyle={tooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ShareTable rows={pieData} firstColumn="Inntektskjelde" hasFylke={!!fylkeLatest} hasLand={!!landLatest} testId="revenue" fylkeLabel={averages?.storby ? "Storbyane" : "Fylke"} />
                {(fylkeLatest || landLatest) && (
                  <p className="text-xs text-[#8AA4BF] mt-2">
                    {fylkeLatest && `${averages?.storby ? "Storbyane" : "Fylke"} = gjennomsnittleg andel for ${fylkeLatest.kommune_count} ${averages?.storby ? "storbyar" : "kommunar i fylket"}. `}
                    {landLatest && "Landet = alle kommunar samla (SSB-aggregat)."}
                  </p>
                )}
              </div>
              <div className="xl:col-span-3">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">Utvikling over tid</p>
                <div className="h-[340px]" data-testid="revenue-stacked-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData}>
                      {endringsLinjer(barData)}
                      <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                      <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                      <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={fmtAxis} />
                      <Tooltip
                        formatter={(v, name, item) => [`${fmt(v)} tusen kr (${pct(v, item.payload._total)})`, name]}
                        contentStyle={tooltipStyle}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />
                      {(shareRows.length ? shareRows : REVENUE_FIELDS.map((f) => ({ ...f, name: f.label }))).map((f) => (
                        <Bar key={f.key} dataKey={f.name} stackId="rev" fill={f.color} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {(fylkeLatest || landLatest) && (
              <ShareCompareChart
                rows={shareRows}
                fylkeName={fylkeLatest ? averages.fylkeName : null}
                snittOrd={averages?.storby ? "storbysnittet" : "fylkessnittet"}
                landName={landLatest ? averages.landName : null}
                year={latest.year}
                subject="andel av driftsinntekter"
                testId="revenue"
                height={360}
              />
            )}
          </div>
        )}
        <p className="text-xs text-[#8AA4BF] mt-4 text-center">
          Kjelde:{" "}
          <a href="https://www.ssb.no/statbank/table/13551" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
            SSB 13551 <ExternalLink className="w-3 h-3" />
          </a>
        </p>
      </CardContent>
    </Card>
  );
};

export default RevenueSection;
